import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Star, Phone, MessageSquare, Mail } from "lucide-react";
import { Input } from "../components/ui/input";
import { Chips } from "../components/Chips";
import { MiniHeader } from "./Landing";
import { getAttribution, trackLead, trackSchedule, trackContact, loadGTM } from "../lib/tracking";
import { DEPOSIT, PHONE_DISPLAY, PHONE_TEL, ADDRESS_LINE1, ADDRESS_LINE2, CHERRY_URL, EMAIL } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TREATMENTS = ["Glass Skin Laser", "RF Microneedling", "CO₂ / Erbium Laser", "Xerf Skin Tightening", "Cosmelan® Peel", "Peels & Facials", "Botox / Xeomin", "Dermal Fillers", "Medical Weight Loss", "Body Contouring", "Permanent Makeup", "Tattoo Removal", "Not sure yet"];
const BUDGETS = ["Under $500", "$500–$1,500", "$1,500–$5,000", "$5,000+"];
const PACKAGE = ["Yes", "Maybe", "Single visit"];
const MEMBERSHIP = ["Yes", "Maybe", "No"];
const field = "h-12 rounded-none border-0 border-b border-neutral-300 bg-transparent px-0 text-base font-light shadow-none focus-visible:border-neutral-950 focus-visible:ring-0";
const lbl = "text-[11px] uppercase tracking-[0.22em] text-neutral-600";
const EMPTY = { name: "", email: "", phone: "", treatments: [], budget: "", package_interest: "", membership_interest: "" };

const BookBtn = ({ className, testid, children = "Book Now" }) => (
  <Link to="/book-online" onClick={trackSchedule} className={className} data-testid={testid}>{children}</Link>
);

const ContactStrip = () => {
  const cls = "flex min-h-[48px] items-center justify-center gap-2 border border-neutral-950 bg-white px-3 text-[11px] font-medium uppercase tracking-[0.18em] text-neutral-950 transition-colors hover:bg-neutral-950 hover:text-white";
  return (
    <div className="mt-8 grid grid-cols-3 gap-2" data-testid="glow-contact-strip">
      <a href={`tel:${PHONE_TEL}`} onClick={trackContact} className={cls} data-testid="glow-call-now"><Phone size={14} strokeWidth={1.5} /> Call Now</a>
      <a href={`sms:${PHONE_TEL}`} onClick={trackContact} className={cls} data-testid="glow-text-now"><MessageSquare size={14} strokeWidth={1.5} /> Text Now</a>
      <a href={`mailto:${EMAIL}`} onClick={trackContact} className={cls} data-testid="glow-email-now"><Mail size={14} strokeWidth={1.5} /> Email</a>
    </div>
  );
};

const CherryBanner = () => (
  <a href={CHERRY_URL} target="_blank" rel="noreferrer" className="group mt-8 flex flex-col items-center gap-3 border border-neutral-950 bg-white px-5 py-5 text-center transition-colors hover:bg-neutral-950 hover:text-white sm:flex-row sm:justify-between sm:text-left" data-testid="glow-cherry-banner">
    <span>
      <span className="block text-[11px] uppercase tracking-[0.25em] text-neutral-600 group-hover:text-neutral-300">Cherry® Financing · Pay over time</span>
      <span className="font-display mt-1 block text-xl">3 minutes to apply · No hard credit check</span>
      <span className="mt-1 block text-sm font-light text-neutral-600 group-hover:text-neutral-300">0% APR options may be available.</span>
    </span>
    <span className="shrink-0 text-[11px] font-medium uppercase tracking-[0.2em] underline underline-offset-4">Apply for Cherry®</span>
  </a>
);

const Thanks = ({ name }) => (
  <div className="rise py-6 text-center" data-testid="glow-thanks">
    <p className="kicker">Request received</p>
    <h2 className="font-display mt-3 text-4xl font-light text-neutral-950">Thank you, {name.split(" ")[0]}.</h2>
    <p className="mx-auto mt-4 max-w-sm text-base font-light leading-relaxed text-neutral-600">Our concierge will reach out shortly. Want your time now? A {DEPOSIT} deposit secures your appointment.</p>
    <BookBtn className="btn-ink mt-8 w-full" testid="glow-thanks-book-btn">Book My Appointment</BookBtn>
    <p className="mt-5 text-sm font-light text-neutral-600"><a href={`tel:${PHONE_TEL}`} className="underline" data-testid="glow-thanks-call">Call</a> or <a href={`sms:${PHONE_TEL}`} className="underline" data-testid="glow-thanks-text">text</a> {PHONE_DISPLAY}</p>
  </div>
);

const GlowForm = ({ onDone }) => {
  const [f, setF] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("Please enter your full name.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) return toast.error("Please enter a valid email address.");
    if (f.phone.replace(/\D/g, "").length < 10) return toast.error("Please enter a valid phone number.");
    if (!f.treatments.length) return toast.error("Please tick at least one treatment.");
    if (!f.budget) return toast.error("Please choose a budget.");
    setSending(true);
    try {
      const attr = getAttribution();
      const { treatments, ...rest } = f;
      await axios.post(`${API}/leads`, { ...rest, treatment: treatments.join(", "), ...attr, page: "glow", source: attr.utm_source || "facebook" });
      trackLead({ content_name: treatments.join(", "), content_category: "glow", value: 0, currency: "USD" });
      onDone(f.name);
    } catch {
      toast.error("We couldn't send your details. Please check them and try again.");
    } finally {
      setSending(false);
    }
  };
  return (
    <form onSubmit={submit} noValidate className="space-y-8" data-testid="glow-form">
      <p className="text-xs font-light text-neutral-600">* Required</p>
      <label className="block"><span className={lbl}>Full Name *</span><Input required autoComplete="name" value={f.name} onChange={set("name")} className={field} data-testid="glow-name-input" /></label>
      <label className="block"><span className={lbl}>Email *</span><Input required type="email" autoComplete="email" value={f.email} onChange={set("email")} className={field} data-testid="glow-email-input" /></label>
      <label className="block"><span className={lbl}>Phone Number *</span><Input required type="tel" inputMode="tel" autoComplete="tel" placeholder="(602) 555-0123" value={f.phone} onChange={set("phone")} className={field} data-testid="glow-phone-input" /></label>
      <div><span className={lbl}>Treatments You're Interested In · tick all that apply</span><Chips multi options={TREATMENTS} value={f.treatments} onChange={set("treatments")} testid="glow-treatment" /></div>
      <div><span className={lbl}>Your Budget</span><Chips options={BUDGETS} value={f.budget} onChange={set("budget")} testid="glow-budget" /></div>
      <div><span className={lbl}>Interested in a package of 3 (save 20%)?</span><Chips options={PACKAGE} value={f.package_interest} onChange={set("package_interest")} testid="glow-package" cols="grid-cols-3" /></div>
      <div><span className={lbl}>Interested in a monthly membership?</span><Chips options={MEMBERSHIP} value={f.membership_interest} onChange={set("membership_interest")} testid="glow-membership" cols="grid-cols-3" /></div>
      <button type="submit" disabled={sending} className="btn-ink w-full disabled:opacity-60" data-testid="glow-submit-btn">{sending ? "Sending…" : "Contact Us"}</button>
      <div className="flex items-center gap-4 text-[11px] uppercase tracking-[0.25em] text-neutral-500"><span className="h-px flex-1 bg-neutral-200" />or<span className="h-px flex-1 bg-neutral-200" /></div>
      <BookBtn className="btn-line w-full" testid="glow-book-direct-btn">Book Now Directly</BookBtn>
    </form>
  );
};

export default function Glow() {
  const [doneName, setDoneName] = useState("");
  useEffect(() => { loadGTM(); }, []);
  return (
    <div className="min-h-screen bg-[hsl(var(--stone))]" data-testid="glow-page">
      <MiniHeader />
      <main className="mx-auto max-w-[620px] px-5 py-12 md:py-16">
        <div className="text-center">
          <div className="flex justify-center gap-1 text-neutral-950" aria-label="5 star rated">{[0, 1, 2, 3, 4].map((i) => <Star key={i} size={14} strokeWidth={1} fill="currentColor" />)}</div>
          <p className="kicker mt-4">Scottsdale's Skin Reset · 5-star rated</p>
          <h1 className="font-display mt-4 text-4xl font-light leading-[1.05] text-neutral-950 sm:text-5xl" data-testid="glow-headline">Claim your <em>personal glow plan.</em></h1>
          <p className="mx-auto mt-5 max-w-md text-base font-light text-neutral-600">Answer a few quick taps about your skin goals. Our team will build a plan around you, with clear pricing, and follow up personally.</p>
          <ContactStrip />
          <BookBtn className="link-under mt-6" testid="glow-top-book-btn">Ready now? Book directly</BookBtn>
          <CherryBanner />
        </div>
        <div className="mt-10 border border-neutral-200 bg-white p-6 sm:p-10">
          {doneName ? <Thanks name={doneName} /> : <GlowForm onDone={setDoneName} />}
        </div>
        <p className="mt-8 text-center text-xs font-light text-neutral-600">Your details stay private and are only used to arrange your visit.</p>
      </main>
      <footer className="bg-neutral-950 px-5 py-8 text-center text-[11px] tracking-[0.15em] text-neutral-400" data-testid="glow-footer">
        Lux Veritae Skin Spa · {ADDRESS_LINE1}, {ADDRESS_LINE2} · <a href={`tel:${PHONE_TEL}`} className="hover:text-white">{PHONE_DISPLAY}</a> · <a href={`mailto:${EMAIL}`} className="hover:text-white" data-testid="glow-footer-email">{EMAIL}</a>
      </footer>
    </div>
  );
}
